import { useCallback, useState } from "react";
import Column from "@amzn/meridian/column";
import Table, {
  TableActionBar,
  TableCell,
  TableRow,
} from "@amzn/meridian/table";
import Pagination from "@amzn/meridian/pagination";
import {
  ACCESS_DENIED_MESSAGE,
  ADD_FAIL_STATUS,
  ADD_SUCCESS_STATUS,
  DEFAULT_SORT_DIRECTION,
  DELETE_FAIL_STATUS,
  DELETE_SUCCESS_STATUS,
  DUPLICATE_PP_RELATIONSHIP,
  FROM_LABEL,
  ITEMS_PER_PAGE,
  PLEASE_WAIT_STATUS,
  PPRELATIONSHIP_HEADERS,
  SORT_CENTER_LABEL,
  TO_LABEL,
  UPDATE_FAIL_STATUS,
  UPDATE_SUCCESS_STATUS,
  USER_ACCESS_KEY,
} from "../../../constants";
import Button from "@amzn/meridian/button";
import PPRelationshipAddForm from "../PPRelationshipAddForm";
import EditablePPRelationshipRow from "./EditablePPRelationshipRow";
import ReadOnlyPPRelationshipRow from "./ReadOnlyPPRelationshipRow";
import { IId, IProcessPathRelationship } from "../../../common/api/api";
import Input from "@amzn/meridian/input";
import filterTokenIcon from "@amzn/meridian-tokens/base/icon/filter";
import AmpApi from "../../../common/api/AmpApi";
import SubmitModal from "../../../components/modal/SubmitModal";
import Loader from "@amzn/meridian/loader";
import Toaster from "@amzn/meridian/toaster";
import Alert from "@amzn/meridian/alert";
import {TableSortDirection} from "@amzn/meridian/table/table";

function PPRelationshipTable({ selectedNode, ppRelList }) {
  //useState for the mhe list
  const [pprList, setPPRList] = useState(ppRelList);
  //useState for filtering table
  const [searchTerm, setSearchTerm] = useState("");
  //For the modal to display message
  const [openModal, setOpenModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const onConfirmClose = useCallback(() => setOpenModal(false), []);
  //For the loading sign for api calls
  const [loading, setLoading] = useState(false);
  //For pagination
  const itemsPerPage = ITEMS_PER_PAGE;
  const numberOfPages = Math.ceil(pprList.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;
  //Pagination
  //For sorting table columns
  const [sortColumn, setSortColumn] = useState(PPRELATIONSHIP_HEADERS.FROM)
  const [sortDirection, setSortDirection] = useState<TableSortDirection>(DEFAULT_SORT_DIRECTION);
  const onSort = useCallback(({sortColumn, sortDirection}) => {
    setSortDirection(sortDirection)
    setSortColumn(sortColumn)
  }, [])
  //For Add Form
  const [isShown, setIsShown] = useState(false);
  const displayAddForm = () => {
    setIsShown((current) => !current);
  };
  const initialState: IProcessPathRelationship = {
    id: "",
    from: "",
    to: "",
  };
  const [addFormData, setAddFormData] = useState(initialState);

  /**
   * This function adds a new Process Path Relationship
   * @param addData The new Process Path Relationship to be added.
   */
  function addFormHandler(addData) {
    let result = pprList.filter(function (v, i) {
      return v.to == addData.to && v.from == addData.from;
    });
    if (result.length > 0) {
      setErrorMessage(DUPLICATE_PP_RELATIONSHIP);
      onOpenToast();
    } else {
      const addList: IProcessPathRelationship[] = [];
      const finalList = [addData, ...addList];
      setLoading(true);
      //Making api call for add
      (async () => {
        try {
          await AmpApi.updatePPRelationship(selectedNode, finalList);
          setLoading(false);
          setOpenModal(true);
          setResponseMessage(ADD_SUCCESS_STATUS);
          const newFormData = { ...addData };
          setAddFormData(newFormData);
          setPPRList([addData, ...pprList]);
        } catch (ex: any) {
          setLoading(false);
          if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
            setErrorMessage(ACCESS_DENIED_MESSAGE);
            onOpenToast();
          }
          else {
            console.error(`Error updating ${selectedNode}`, ex);
            setOpenModal(true);
            setResponseMessage(ADD_FAIL_STATUS);
          }
        }
      })();
      //End of the api call for add
    }
  }

  //UseState to edit row data
  const [editPPRId, setEditPPRId] = useState(null);
  const [editFormData, setEditFormData] = useState(initialState);

  /**
   * This function Updates the process path relationship
   * @param newFormData Object containing the updated data
   */
  function handleEditFormChange(newFormData) {
    let result = pprList.filter(function (v, i) {
      return v.to == newFormData.to && v.from == newFormData.from;
    });
    if (result.length > 0 && result[0].id != newFormData.id) {
      setErrorMessage(DUPLICATE_PP_RELATIONSHIP);
      onOpenToast();
    } else {
      const newEditPPRList = [...pprList];
      const updateList: IProcessPathRelationship[] = [];
      const finalList = [newFormData, ...updateList];
      setOpenModal(true);
      setResponseMessage(PLEASE_WAIT_STATUS);
      //Making api call for update
      (async () => {
        try {
          await AmpApi.updatePPRelationship(
            selectedNode,
            finalList
          );
          setResponseMessage(UPDATE_SUCCESS_STATUS);
          setEditFormData(newFormData);
          const index = newEditPPRList.findIndex((obj) => {
            return obj.id === newFormData.id;
          });
          newEditPPRList[index] = newFormData;
          setPPRList(newEditPPRList);
          setEditPPRId(null);
        } catch (ex: any) {
          if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
            setOpenModal(false);
            setErrorMessage(ACCESS_DENIED_MESSAGE);
            onOpenToast();
          }
          else {
            console.error(`Error updating ${selectedNode}`, ex);
            setOpenModal(true);
            setResponseMessage(UPDATE_FAIL_STATUS);
          }
        }
      })();
      //End of the api call for update
    }
  }

  const handleEditClick = (ppr) => {
    setEditPPRId(ppr.id);
    const formValues: IProcessPathRelationship = {
      id: ppr.id,
      from: ppr.from,
      to: ppr.to,
    };
    setEditFormData(formValues);
  };
  /**
   * To handle the delete button
   * @param ppr The Process Path Relationship object to be deleted
   */
  const handleDeleteClick = (ppr) => {
    const newPPRList = [...pprList];
    const idsList: IId[] = [];
    const finalIds: IId[] = [ppr, ...idsList];
    setOpenModal(true);
    setResponseMessage(PLEASE_WAIT_STATUS);
    //Delete api call starts
    (async () => {
      try {
        await AmpApi.deletePPRelationship(selectedNode, finalIds);
        setResponseMessage(DELETE_SUCCESS_STATUS);
        const index = newPPRList.findIndex((obj) => {
          return obj.id === ppr.id;
        });
        newPPRList.splice(index, 1);
        setPPRList(newPPRList);
      } catch (ex: any) {
        if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
          setOpenModal(false);
          setErrorMessage(ACCESS_DENIED_MESSAGE);
          onOpenToast();
        } else {
          console.error(`Error deleting ${ppr}`, ex);
          setOpenModal(true);
          setResponseMessage(DELETE_FAIL_STATUS);
        }
      }
    })();
    //Delete api call ends
  };
  /**
   * To handle the cancel button
   */
  const cancelHandler = () => {
    setIsShown((current) => !current);
  };
  const handleCancelClick = () => {
    setEditPPRId(null);
  };

  //To get a list of unique ProcessPath names
  let processPathsTo = ppRelList.map((a) => a.to);
  let uniqueProcessPathTo = [...new Set(processPathsTo)];
  let processPathsFrom = ppRelList.map((a) => a.from);
  let uniqueProcessPathFrom = [...new Set(processPathsFrom)];
  let processpaths = [...uniqueProcessPathTo, ...uniqueProcessPathFrom];
  let uniqueProcessPaths = [...new Set(processpaths)];
  //For duplicate records error
  let dummyId = 0;
  const [errorMessage, setErrorMessage] = useState("");
  const [toasts, setToasts] = useState([]);
  const onCloseToast = useCallback(
    (id) => setToasts(toasts.filter((t) => t.id !== id)),
    [toasts]
  );
  const onOpenToast = useCallback(
    () => setToasts(toasts.concat({ id: `${++dummyId}` })),
    [toasts]
  );

  ppRelList.sort((a, b) => {
    if (a[sortColumn] < b[sortColumn]) {
      return sortDirection === "ascending" ? -1 : 1
    }
    if (a[sortColumn] > b[sortColumn]) {
      return sortDirection === "ascending" ? 1 : -1
    }
    return 0
  })

  return (
    <>
      {errorMessage && (
        <Toaster toasts={toasts} onCloseToast={onCloseToast}>
          {(toast) => (
            <Alert toast={true} onClose={toast.onClose} type="error">
              {errorMessage}
            </Alert>
          )}
        </Toaster>
      )}
      <SubmitModal
        open={openModal}
        responseMessage={responseMessage}
        onClose={onConfirmClose}
      />
      {isShown && (
        <Column>
          {loading ? (
            <Loader />
          ) : (
            <PPRelationshipAddForm
              addFormHandler={addFormHandler}
              cancelHandler={cancelHandler}
              uniqueProcessPaths={uniqueProcessPaths}
              selectedNode={selectedNode}
              ppRelList={ppRelList}
            />
          )}
        </Column>
      )}
      <Column>
        <Table
            headerRows={1}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
            spacing="small"
        >
          <TableActionBar>
            <Button onClick={displayAddForm}>
              {isShown ? "Close" : "Add New"}
            </Button>
            <Input
              key="searchInput"
              type="text"
              value={searchTerm}
              placeholder="Search by Process Path Name"
              prefixIconTokens={filterTokenIcon}
              onChange={setSearchTerm}
            />
          </TableActionBar>
          <TableRow>
            <TableCell width={100}>{SORT_CENTER_LABEL}</TableCell>
            <TableCell sortColumn={PPRELATIONSHIP_HEADERS.FROM} width={200}>{FROM_LABEL}</TableCell>
            <TableCell sortColumn={PPRELATIONSHIP_HEADERS.TO} width={200}>{TO_LABEL}</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </Table>
        {pprList
          .filter((ppr) => {
            if (searchTerm == "") {
              return ppr;
            } else if (
              ppr.to.toLowerCase().includes(searchTerm.toLowerCase()) ||
              ppr.from.toLowerCase().includes(searchTerm.toLowerCase())
            ) {
              return ppr;
            }
          })
          .slice(firstVisibleIndex, lastVisibleIndex)
          .map((ppr) => (
            <div key={ppr.id}>
              {editPPRId === ppr.id ? (
                <EditablePPRelationshipRow
                  ppr={ppr}
                  selectedNode={selectedNode}
                  handleEditFormChange={handleEditFormChange}
                  handleCancelClick={handleCancelClick}
                  uniqueProcessPaths={uniqueProcessPaths}
                />
              ) : (
                <ReadOnlyPPRelationshipRow
                  ppr={ppr}
                  selectedNode={selectedNode}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                />
              )}
            </div>
          ))}
        <Pagination
          showSkipArrows={true}
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Column>
    </>
  );
}

export default PPRelationshipTable;
